// An Expected Charge Item's demand unit, and the name it is shown and matched by.
//
// A demand charge is billed per kW or per kVA, and the same charge exists in
// both: "Peak Demand - kW" and "Peak Demand - kVA" are different expected
// lines. The network tariff names its buckets that way (bv_site_network.name),
// so an item named the same pairs with the expected line by name alone:
//
//   plain name          Peak Demand          + kW  -> Peak Demand - kW
//   " - qualifier" name Peak Demand - Annual + kVA -> Peak Demand - Annual kVA
//
// Shared by the migration's backfill, the charge-map routes, the admin form and
// the workbook loader, so all of them spell a unit item the same way.

export const DEMAND_UNITS = ['kW', 'kVA'] as const;
export type DemandUnit = (typeof DEMAND_UNITS)[number];

const UNIT_SUFFIX = /^(.*?)\s*(?:-\s*|\s)(kW|kVA)\s*$/i;

const canonicalUnit = (u: string): DemandUnit | null =>
  DEMAND_UNITS.find((d) => d.toLowerCase() === u.trim().toLowerCase()) ?? null;

/** The base name and the unit a name ends in, if any ("- kVA", " kW", any case). */
export function splitItemUnit(name: string): { base: string; unit: DemandUnit | null } {
  const m = UNIT_SUFFIX.exec(name.trim());
  if (!m) return { base: name.trim(), unit: null };
  return { base: m[1].trim(), unit: canonicalUnit(m[2]) };
}

/** The item's name with its unit, spelt the way the network tariff spells it. */
export function itemNameWithUnit(name: string, unit: DemandUnit | null): string {
  const { base } = splitItemUnit(name);
  if (!unit) return name.trim();
  return /\s-\s\S/.test(base) ? `${base} ${unit}` : `${base} - ${unit}`;
}

/** The workbook's Unit column: "kVA or kW" is one item per unit. */
export function unitsFromSheet(cell: string | null | undefined): (DemandUnit | null)[] {
  const units = String(cell ?? '')
    .split(/\bor\b|[,/]/i)
    .map(canonicalUnit)
    .filter((u): u is DemandUnit => !!u);
  return units.length > 0 ? units : [null];
}
